/**
 * RolesView — grant/revoke RBAC roles for a user (Phase 4/5).
 *
 * Admin picks a user, sees that user's current role assignments, and
 * grants a new role (optionally team-scoped) or revokes one. Gated on
 * team.manage (Firm Admin or Team Lead); the backend enforces the finer
 * no-escalation / team-authority rules and returns typed 403s we surface.
 */
import React, { useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'sonner';

import IconWrapper from '../../IconWrapper';
import { Heading } from '../../ui/heading';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useAccess, Capability } from '../../../hooks/useAccess';
import { useTeams } from '../../../hooks/useTeams';
import { useFirmUsers } from '../../../hooks/useFirmUsers';
import {
  useUserRoles,
  useGrantRole,
  useRevokeRole,
} from '../../../hooks/useTeamAdmin';

/** Client roles an admin assigns from the UI. Internal (GO Lab) roles are
 * managed by Platform Admins elsewhere. team_id requirement mirrors the
 * backend RoleDef.team_scope. */
const CLIENT_ROLES = [
  { key: 'VIEWER', teamScope: 'optional' as const },
  { key: 'END_USER', teamScope: 'required' as const },
  { key: 'TEAM_LEAD', teamScope: 'required' as const },
  { key: 'FIRM_ADMIN', teamScope: 'forbidden' as const },
];

/** Human-friendly display names for role keys. The stored/enum keys are
 * unchanged (FIRM_ADMIN, END_USER); this only affects what users see. */
const ROLE_LABELS: Record<string, string> = {
  VIEWER: 'Viewer',
  END_USER: 'User',
  TEAM_LEAD: 'Team Lead',
  FIRM_ADMIN: 'Organization Admin',
};

const roleLabel = (key: string): string => ROLE_LABELS[key] ?? key;

const RolesView: React.FC = () => {
  const { can } = useAccess();
  const canManage = can(Capability.FirmManage);
  const { data: teams } = useTeams();
  const { data: users } = useFirmUsers();
  const [committedUserId, setCommittedUserId] = useState('');
  const [role, setRole] = useState('END_USER');
  const [teamId, setTeamId] = useState('');

  const { data: roles, isLoading } = useUserRoles(committedUserId);
  const grant = useGrantRole(committedUserId);
  const revoke = useRevokeRole(committedUserId);

  const scope = CLIENT_ROLES.find((r) => r.key === role)?.teamScope;

  const handleGrant = async () => {
    if (!committedUserId) return;
    try {
      await grant.mutateAsync({
        role,
        teamId: scope === 'forbidden' ? null : teamId || null,
      });
      toast.success(`Granted ${roleLabel(role)}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to grant role');
    }
  };

  const handleRevoke = async (assignmentId: string) => {
    try {
      await revoke.mutateAsync({ assignmentId });
      toast.success('Role revoked');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke role');
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <Heading level={2}>Roles</Heading>
        <p className="mt-2 text-sm text-muted-foreground">
          You need organization administration access to assign roles.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Heading level={2}>Roles</Heading>
        <p className="mt-1 text-sm text-muted-foreground">
          Grant or revoke a user&apos;s roles. Pick a user to begin.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>User</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            className="border rounded-md px-3 py-2 text-sm bg-background w-full max-w-md"
            value={committedUserId}
            onChange={(e) => setCommittedUserId(e.target.value)}
            aria-label="Select a user"
          >
            <option value="">Select a user…</option>
            {users?.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.username}
                {u.email ? ` (${u.email})` : ''}
                {u.isActive ? '' : ' — locked'}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {committedUserId && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Grant a role</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2 items-center">
              <select
                className="border rounded-md px-3 py-2 text-sm bg-background"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {CLIENT_ROLES.map((r) => (
                  <option key={r.key} value={r.key}>{roleLabel(r.key)}</option>
                ))}
              </select>
              {scope !== 'forbidden' && (
                <select
                  className="border rounded-md px-3 py-2 text-sm bg-background"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  <option value="">
                    {scope === 'required' ? 'Select a team…' : 'Organization-wide (no team)'}
                  </option>
                  {teams?.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              )}
              <Button
                onClick={handleGrant}
                loading={grant.isPending}
                disabled={scope === 'required' && !teamId}
                leftIcon={<IconWrapper icon={FiPlus} />}
              >
                Grant
              </Button>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Heading level={4}>Current roles</Heading>
            {isLoading && (
              <p className="text-sm text-muted-foreground">Loading…</p>
            )}
            {roles && roles.length === 0 && (
              <p className="text-sm text-muted-foreground">No roles assigned.</p>
            )}
            {roles?.map((r) => (
              <Card key={r.id}>
                <CardContent className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2">
                    <Badge>{roleLabel(r.role)}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {r.teamId
                        ? teams?.find((t) => t.id === r.teamId)?.name ?? r.teamId.slice(0, 8)
                        : 'organization-wide'}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRevoke(r.id)}
                    loading={revoke.isPending}
                    leftIcon={<IconWrapper icon={FiTrash2} />}
                  >
                    Revoke
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RolesView;
